import DOMPurify, { Config as DOMPurifyConfig } from "dompurify";

import React from "react";

// Allow-list approach: only explicitly permitted tags and attributes pass through.
// Anything not listed is stripped by DOMPurify (text content is preserved, tags are not).
//
// img/src/alt are included for now pending confirmation of real-world usage in content.
// If unused, drop "img", "src", and "alt" to eliminate the tracking-pixel tradeoff.
const SANITIZE_CONFIG: DOMPurifyConfig = {
  ALLOWED_TAGS: [
    "a",
    "b",
    "br",
    "em",
    "i",
    "img",
    "li",
    "ol",
    "p",
    "span",
    "strong",
    "ul",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
  ],
  ALLOWED_ATTR: ["alt", "href", "rel", "src"],
  ALLOWED_URI_REGEXP: /^https?:/i,
};

type SafeHtmlOwnProps<T extends React.ElementType> = {
  as?: T;
  html: string;
  className?: string;
};

type SafeHtmlProps<T extends React.ElementType> = SafeHtmlOwnProps<T> &
  Omit<React.ComponentPropsWithoutRef<T>, keyof SafeHtmlOwnProps<T> | "dangerouslySetInnerHTML" | "children">;

/**
 * Renders sanitized HTML content using DOMPurify to prevent XSS vulnerabilities.
 * By default, it renders a <div>, but the tag can be customized via the `as` prop.
 * Only a specific set of tags and attributes are allowed, and all others are stripped out.
 *
 * @example
 * <SafeHtml html="<p>Hello <strong>world</strong>!</p>" />
 */
const SafeHtml = <T extends React.ElementType>({
  html,
  as,
  className,
  ...restProps
}: SafeHtmlProps<T>): JSX.Element => {
  const TagType = as || "div";
  return (
    <TagType
      className={className}
      dangerouslySetInnerHTML={{
        __html: DOMPurify.sanitize(html, SANITIZE_CONFIG),
      }}
      {...restProps}
    />
  );
};

export default SafeHtml;
